import BemClass from '@upsales/components/Utils/bemClass';
import { isAvailable } from 'App/services/FeatureHelperStandalone';
import React, { useState, useEffect, ComponentProps, useRef } from 'react';
import './AppSidebar.scss';
import { Block, Card, Icon, IconName, Link, OutsideClick, Text, ClickableItem, Tooltip } from '@upsales/components';
import { CancelablePromise, makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import Links from 'App/resources/Links';
import logError from 'Helpers/logError';
import { RootState } from 'Store/index';
import history from 'App/pages/routes/history';
import { useAppDispatch, useFeatureAvailable, useSelector, useSoftDeployAccess } from '../hooks';
import { useMetadata } from '../hooks/appHooks';
import { useTranslation } from 'Components/Helpers/translate';
import LinkType from 'App/resources/Model/Links';
import { Feature } from '../hooks/featureHelper';
import { globalTracker } from 'App/babel/helpers/Tracker';
import InAppChat from 'Services/InAppChat';
import { SidebarSavedModalButton, SidebarSavedLocalStorageModalButton } from '../SidebarSavedModalButton';
import { setSidebarLinks, setAgentPages, addAssistantUnread } from 'Store/actions/AppActions';
import AgentPagesResource from 'Resources/AgentPages';
import { getVisibleNavSections } from 'App/helpers/navSections';
import { AppState } from 'Store/reducers/AppReducer';
import useCleanMyDataNotification from 'App/components/CleanMyData/useCleanMyDataNotification';
import { useAssistantPermissions } from '../hooks/useAssistantPermissions';
import config from 'App/babel/config';
import { chatOpenStore } from 'App/pages/FloatingAIChat/useChatOpen';
import { useAddActions } from './useAddActions';
import { useFloatingChatAccess } from 'App/pages/FloatingAIChat/useFloatingChatAccess';
import { useInboxCount } from 'App/pages/FloatingAIChat/useInboxCount';
import EventBus from 'App/services/EventBus';

const SidebarButton = ({
	active,
	notification,
	notificationCount,
	icon,
	loading = false,
	renderDropdown,
	onClick,
	tooltip
}: {
	icon: ComponentProps<typeof Icon>['name'];
	loading?: boolean;
	active?: boolean;
	notification?: boolean;
	notificationCount?: number;
	renderDropdown?: (close: () => void) => JSX.Element;
	onClick?: () => void;
	tooltip: string;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const clickedOutside = useRef(false);
	const { t } = useTranslation();
	const doClick = () => {
		onClick?.();
		if (renderDropdown) {
			if (!clickedOutside.current && !isOpen) {
				setIsOpen(true);
			} else if (clickedOutside.current && !isOpen) {
				clickedOutside.current = false;
			} else {
				clickedOutside.current = false;
				setIsOpen(true);
			}
		}
	};
	const doOutsideClick = () => {
		if (isOpen) {
			clickedOutside.current = true;
			setIsOpen(false);
		} else {
			clickedOutside.current = false;
		}
	};
	const classes = new BemClass('AppSidebar__button-wrap').mod({ active });

	return (
		<div className={classes.b()}>
			<Tooltip title={t(tooltip)} position="right" distance={24} disabled={loading}>
				<ClickableItem
					block
					icon={icon}
					borderRadius
					color="grey-1"
					onClick={() => doClick()}
					loading={loading}
					active={isOpen}
				/>
				{active ? <Icon name="exclamation" className={classes.elem('active-icon').b()} /> : null}
				{notification ? <Icon color="white" name="exclamation" className={'notification-badge'} /> : null}
				{notificationCount ? (
					<span className="notification-badge">{notificationCount > 9 ? '9+' : notificationCount}</span>
				) : null}
			</Tooltip>
			{renderDropdown ? (
				<OutsideClick outsideClick={doOutsideClick} targetClass="AppSidebar__dropdown">
					<dialog className={new BemClass('AppSidebar__dropdown').b()} open={isOpen}>
						<Card>{renderDropdown(() => setIsOpen(false))}</Card>
					</dialog>
				</OutsideClick>
			) : null}
		</div>
	);
};

const openChat = function (accountSelf: AppState['accountSelf']) {
	if (!InAppChat.isOpened()) {
		InAppChat.open();
	}
};

// Isolated so the inbox poll only mounts behind the showFloatingChat gate (no getInbox() for non-access users).
const AiChatSidebarButton = () => {
	const inboxCount = useInboxCount();
	return (
		<SidebarButton
			data-testid="ai-chat"
			icon="comment-alt"
			tooltip="default.aiChat"
			notificationCount={inboxCount || undefined}
			onClick={() => chatOpenStore.toggle()}
		/>
	);
};

const AppSidebar = () => {
	const classes = new BemClass('AppSidebar');
	const { t } = useTranslation();
	const [loadingLinks, setLoadingLinks] = useState(true);
	const dispatch = useAppDispatch();
	const [chatUnread, setChatUnread] = useState(0);
	const displayCalendar = isAvailable(Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasHelp = (window as any)._elev !== undefined;
	const linksPromise = useRef<CancelablePromise<{ data: LinkType[] }> | null>(null);
	const { self, links, accountSelf, agentPages, accessRights } = useSelector(({ App }: RootState) => ({
		self: App.self,
		links: App.sidebarLinks,
		accountSelf: App.accountSelf,
		agentPages: App.agentPages,
		accessRights: App.accessRights
	}));
	// WEB-6701: layered per-user opt-in, answered by the server for admins too
	// since one can be excluded (WEB-15561). Server-side enforcement is the
	// source of truth — hiding here is best-effort while permissions load.
	const assistantPerms = useAssistantPermissions();
	const renderedNavSections = getVisibleNavSections(
		accountSelf,
		self,
		accessRights,
		assistantPerms?.canAccessWorkspace === true
	);
	const [shouldShowCleanMyDataNotificationBadge, openCleaningModal] = useCleanMyDataNotification(self);

	const addActions = useAddActions();
	const hasChat = InAppChat.isEnabledAndLoaded();

	const isAdmin = self?.administrator;
	const hasWorkflows = useSoftDeployAccess('WORKFLOWS');
	const hasAI = useFeatureAvailable(Feature.AI);
	const hasAgentUserAccess = self?.userParams.agentUserAccess;
	const isEuOnly = useMetadata()?.euOnly === true;
	const showAIAgent = (isAdmin || hasAgentUserAccess) && config.WORKFLOW_URL && hasWorkflows && hasAI && !isEuOnly;
	// WEB-6595: AI Workspace requires the `aiAssistant` feature (Upsales AI
	// tier). AI_ASSISTANT soft-deploy is the internal escape hatch — when
	// removed, only the feature gates access.
	const hasAiAssistant = useFeatureAvailable(Feature.AI_ASSISTANT);
	const hasAIAssistantSoftDeploy = useSoftDeployAccess('AI_ASSISTANT');
	const userCanAccessWorkspace = assistantPerms?.canAccessWorkspace === true;
	const hasWorkspacePage = useSoftDeployAccess('AI_WORKSPACE_PAGE');
	const showAIAssistant = (hasAiAssistant || hasAIAssistantSoftDeploy) && userCanAccessWorkspace && !hasWorkspacePage;
	const showFloatingChat = useFloatingChatAccess();
	// WEB-10482: same AI licence as the workspace above, not `Feature.AI` — an
	// account on the `AI_ASSISTANT` flag or an `UPSALES_AI` trial has AI without
	// the Accelerate-level `ai` feature.
	const showProspectingAgent = (hasAiAssistant || hasAIAssistantSoftDeploy) && !hasWorkspacePage;

	// Published standalone Pages surface in the Links dropdown so anyone can find
	// them after publish (clientCard/orderModal pages live on their records
	// instead). Reads are open to all users; only authoring is gated.
	//
	// No feature-flag check (WEB-10014): a Page we built for a customer has to
	// appear for them even though they don't have Pages themselves. The server
	// returns only what this user may see, and an account with no pages just gets
	// an empty list.
	useEffect(() => {
		if (!self) return;
		let cancelled = false;
		const load = () => {
			AgentPagesResource.find({ sort: [{ a: 'name', s: 'A' }] })
				.then(res => {
					if (cancelled) return;
					const rows = Array.isArray(res?.data) ? res.data : [];
					dispatch(setAgentPages(rows.filter(p => p.status === 'active')));
				})
				.catch(e => logError(e, 'Failed to load published pages'));
		};
		load();
		// Publish/unpublish/delete in the Pages editor fires this so the links
		// dropdown updates without a full page refresh.
		window.addEventListener('upsales:agent-pages-changed', load);
		return () => {
			cancelled = true;
			window.removeEventListener('upsales:agent-pages-changed', load);
		};
	}, [self, dispatch]);

	// A navTab page whose section the navbar doesn't render (the account lacks the
	// feature, or the user lacks access) would otherwise be reachable only by URL,
	// so it falls back into this dropdown alongside the standalone ones.
	const publishedPages = agentPages.filter(
		p => p.placement === 'standalone' || (p.placement === 'navTab' && !renderedNavSections.includes(p.navSection!))
	);

	useEffect(() => {
		let unregisterChatCount: () => void;
		let unregisterAssistant: (() => void) | undefined;

		if (self) {
			// Get links
			// TODO: Put in redux so we can get updated links on add/remove
			linksPromise.current = makeCancelable(Links.find('sidebar', self.id));

			linksPromise.current.promise
				.then(res => {
					dispatch(setSidebarLinks(res.data));
					setLoadingLinks(false);
				})
				.catch(e => logError(e, 'Failed to load links'));

			if (hasChat) {
				unregisterChatCount = InAppChat.on('unreadCount', function (unreadCount: number) {
					setChatUnread(unreadCount);
				});
			}

			unregisterAssistant = EventBus.on(
				'userPrivate.assistant.unread',
				(_e: unknown, data: { conversationId: number }) => {
					if (data?.conversationId) {
						dispatch(addAssistantUnread(data.conversationId));
					}
				}
			);
		}
		return () => {
			linksPromise.current?.cancel();
			unregisterChatCount?.();
			unregisterAssistant?.();
		};
	}, [hasChat]);

	return (
		<div className={classes.b()}>
			<SidebarButton
				icon="plus"
				loading={loadingLinks}
				tooltip="sidebar.tooltip.add"
				renderDropdown={close => {
					return (
						<div className={classes.elem('add-dropdown').b()}>
							<Block space="mtl mrl mll mbl">
								<Text bold size="sm">
									{t('default.add')}
								</Text>
							</Block>
							{addActions.map(action =>
								action.show ? (
									<ClickableItem
										block
										icon={action.icon}
										key={action.type}
										onClick={() => {
											globalTracker.track('add', { type: action.type, location: 'sidebar' });
											action.do();
											close();
										}}
										title={t(action.title)}
									/>
								) : null
							)}
						</div>
					);
				}}
			/>
			{displayCalendar ? (
				<SidebarButton
					icon="calendar"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.calendar"
					onClick={() => {
						history.push(`/calendar/?date=${new Date().toJSON()}&selected=workWeek`);
					}}
				/>
			) : null}
			{links.length || publishedPages.length ? (
				<SidebarButton
					icon="link"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.dynamiclinks"
					renderDropdown={close => {
						return (
							<div className={classes.elem('dynamiclinks-dropdown').b()}>
								{links.length ? (
									<Block space="mtl mrl mll mbl">
										<Text bold size="sm">
											{t('default.links')}
										</Text>
									</Block>
								) : null}
								{links.map(link => (
									<Link nostyle key={link.id} href={link.href} target={link.target}>
										<ClickableItem
											block
											renderLeft={() =>
												link.icon.includes('fa-') ? null : (
													<img className={classes.elem('link-img').b()} src={link.icon} />
												)
											}
											icon={
												link.icon.includes('fa-')
													? (link.icon.replace('fa-', '') as IconName)
													: undefined
											}
											iconStyle={{ color: '#' + link.color }}
											title={link.value}
											onClick={() => {
												close();
											}}
										/>
									</Link>
								))}
								{/* Published standalone Pages — how users find them after
								    publish (WEB-7818 follow-up). */}
								{publishedPages.length ? (
									<>
										<Block space="mtl mrl mll mbl">
											<Text bold size="sm">
												{t('assistant.pages.title')}
											</Text>
										</Block>
										{publishedPages.map(page => (
											<ClickableItem
												block
												key={`page-${page.id}`}
												icon="file-text"
												title={page.name || t('assistant.pages.untitled')}
												onClick={() => {
													history.push(`/pages/${page.slug}`);
													close();
												}}
											/>
										))}
									</>
								) : null}
							</div>
						);
					}}
				/>
			) : null}
			{hasHelp ? (
				<SidebarButton
					icon="question"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.help"
					onClick={() => window._elev.openModule?.('articles')}
				/>
			) : null}
			{hasChat ? (
				<SidebarButton
					icon="comment"
					active={chatUnread > 0}
					loading={loadingLinks}
					tooltip="sidebar.tooltip.chat"
					onClick={() => openChat(accountSelf)}
				/>
			) : null}
			{isAdmin ? (
				<SidebarButton
					data-testid="cleanMyDataButton"
					icon="broom"
					loading={loadingLinks}
					tooltip="sidebar.tooltip.cleanMyData"
					onClick={() => openCleaningModal()}
					notification={shouldShowCleanMyDataNotificationBadge}
				/>
			) : null}
			{showAIAgent ? (
				<SidebarButton
					data-testid="workflows"
					icon="ai-agent"
					tooltip="default.aiAgent"
					onClick={() => history.push('/ai-agent')}
				/>
			) : null}
			{showAIAssistant ? (
				<SidebarButton
					data-testid="ai-workspace"
					icon="stars"
					tooltip="default.aiWorkspace"
					onClick={() => history.push('/ai-workspace')}
				/>
			) : null}
			{showFloatingChat ? <AiChatSidebarButton /> : null}
			{showProspectingAgent ? (
				<SidebarButton
					data-testid="prospecting-agent"
					icon="globe"
					tooltip="Prospecting Agent"
					onClick={() => history.push('/prospecting-agent')}
				/>
			) : null}
			{/*
				UIElements never worked in the old sidebar,
				so i will fix it in another ticket and spend some time to make it fit in properly
			 */}
			{/* <UiElements elements={uiElements} type="sidebar" /> */}
			<SidebarSavedModalButton />
			<SidebarSavedLocalStorageModalButton />
		</div>
	);
};

export default AppSidebar;
